import { useEffect, useMemo, useState } from "react";
import { api, getProductionId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Tag as TagIcon, Bell, Plus, Save, Trash2, Pencil, Palette,
} from "lucide-react";
import CalendarSync from "@/components/CalendarSync";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };
const SWATCH = [
  "#F59E0B", "#EF4444", "#3B82F6", "#10B981", "#8B5CF6",
  "#EC4899", "#14B8A6", "#F97316", "#A3A3A3", "#EAB308",
];

const CADENCE = [
  { value: "off", label: "Off — no digests" },
  { value: "daily", label: "Daily — one email each morning" },
  { value: "weekly", label: "Weekly — one email on Monday" },
];

const NOTIF_TOGGLES = [
  { key: "notify_notes", label: "When I'm tagged in a note", hint: "Cast & rehearsal notes that mention you." },
  { key: "notify_announcements", label: "New announcements", hint: "Broadcast messages from producers or admins." },
  { key: "notify_conflicts", label: "Conflict status updates", hint: "When a conflict you submitted is reviewed." },
  { key: "notify_assignments", label: "Actionables assigned to me", hint: "Tasks handed to you off a rehearsal report." },
  { key: "notify_reports", label: "New rehearsal reports", hint: "Every time the SM publishes a nightly report." },
  { key: "weekly_digest_subscribe", label: "Weekly rehearsal digest", hint: "Sunday-morning summary of the week's calls you're on." },
  { key: "notify_rehearsal_changes", label: "Rehearsal changes & cancellations", hint: "Email me if a rehearsal I'm called to moves, is cancelled, or my call is removed." },
  { key: "notify_rehearsal_reminders", label: "Morning-of rehearsal reminder", hint: "A short nudge at 7 AM on days you have a call so nothing slips." },
];

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Settings() {
  const { user, productionId: ctxPid, production } = useAuth();
  const productionId = ctxPid || getProductionId();
  const canManageTags = ["org_admin", "production_staff", "super_admin"].includes(user?.role);
  const canManageDigest = ["org_admin", "production_staff", "super_admin"].includes(user?.role);

  const [tags, setTags] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: "", color: "#F59E0B", description: "" });
  // Iter 76 — weekly digest schedule fields, admin-only. Kept in state so
  // the toggle + selects stay responsive; PATCH /productions/{id} persists.
  const [digest, setDigest] = useState({
    weekly_digest_enabled: true,
    weekly_digest_day: 0,
    weekly_digest_hour: 9,
    weekly_digest_timezone: "America/New_York",
  });
  const [digestSaving, setDigestSaving] = useState(false);

  useEffect(() => {
    if (production) {
      setDigest({
        weekly_digest_enabled: production.weekly_digest_enabled !== false,
        weekly_digest_day: production.weekly_digest_day ?? 0,
        weekly_digest_hour: production.weekly_digest_hour ?? 9,
        weekly_digest_timezone: production.weekly_digest_timezone || "America/New_York",
      });
    }
  }, [production]);

  const saveDigest = async (updates) => {
    if (!productionId) return;
    setDigest((cur) => ({ ...cur, ...updates }));
    setDigestSaving(true);
    try {
      await api.patch(`/productions/${productionId}`, updates);
      toast.success("Digest schedule saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally { setDigestSaving(false); }
  };

  const load = async () => {
    if (!productionId) return;
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        api.get("/tags", { params: { production_id: productionId } }),
        api.get("/notification-preferences", { params: { production_id: productionId } }),
      ]);
      setTags(t.data || []);
      setPrefs(p.data);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [productionId]);

  const openCreate = () => {
    setEditing(null);
    setForm({ label: "", color: "#F59E0B", description: "" });
    setTagOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ label: t.label, color: t.color || "#F59E0B", description: t.description || "" });
    setTagOpen(true);
  };
  const saveTag = async () => {
    const label = form.label.trim();
    if (!label) return toast.error("Label is required");
    try {
      if (editing) {
        await api.patch(`/tags/${editing.id}`, { ...form, label });
        toast.success("Tag updated");
      } else {
        await api.post("/tags", { ...form, label }, { params: { production_id: productionId } });
        toast.success("Tag created");
      }
      setTagOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const deleteTag = async (t) => {
    if (!confirm(`Delete the "${t.label}" tag?`)) return;
    try {
      await api.delete(`/tags/${t.id}`);
      toast.success("Tag removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const patchPrefs = async (updates) => {
    setPrefs((cur) => ({ ...(cur || {}), ...updates }));
    try {
      const r = await api.patch("/notification-preferences", updates, { params: { production_id: productionId } });
      setPrefs(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save preferences");
      load();
    }
  };

  const sortedTags = useMemo(
    () => [...tags].sort((a, b) => a.label.localeCompare(b.label)),
    [tags]
  );

  return (
    <div className="space-y-8 animate-fade-up" data-testid="settings-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Configuration</div>
        <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Settings</h1>
        <p className="mt-1 text-zinc-400 text-sm max-w-2xl">
          Organization-level configuration that isn&apos;t tied to a specific show setup.
          Manage the tag vocabulary your company uses to label notes and reports, and
          fine-tune the email digests you receive from StageCall.
        </p>
      </header>

      {/* Tags */}
      <section
        data-testid="settings-tags-section"
        className="rounded-lg border border-white/10 bg-[#121212] p-6"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md grid place-items-center bg-gold/10 text-gold">
                <TagIcon className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display text-xl leading-tight">Tags &amp; Categories</div>
                <div className="text-xs text-zinc-500">
                  Colour-coded labels for notes, reports, and follow-ups.
                </div>
              </div>
            </div>
          </div>
          {canManageTags && (
            <button
              data-testid="settings-new-tag-btn"
              onClick={openCreate}
              style={goldBtn}
              className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110 inline-flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> New tag
            </button>
          )}
        </div>

        <div className="mt-5">
          {loading ? (
            <div className="text-xs text-zinc-500 py-6 text-center">Loading tags…</div>
          ) : sortedTags.length === 0 ? (
            <div className="rounded border border-dashed border-white/10 py-10 text-center text-sm text-zinc-500">
              No tags yet. {canManageTags ? "Create your first one to start categorizing." : "Ask an admin to set some up."}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="settings-tags-grid">
              {sortedTags.map((t) => (
                <div
                  key={t.id}
                  data-testid={`settings-tag-${t.id}`}
                  className="rounded-md border border-white/10 bg-[#0A0A0A] p-3 flex items-start gap-3 group"
                >
                  <span
                    className="mt-1 w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: t.color || "#F59E0B" }}
                    aria-label="tag colour"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{t.label}</div>
                    {t.description && (
                      <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{t.description}</div>
                    )}
                  </div>
                  {canManageTags && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        data-testid={`settings-tag-edit-${t.id}`}
                        onClick={() => openEdit(t)}
                        title="Edit tag"
                        className="p-1.5 rounded text-zinc-400 hover:text-gold hover:bg-white/5"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`settings-tag-delete-${t.id}`}
                        onClick={() => deleteTag(t)}
                        title="Delete tag"
                        className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-white/5"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Notification Preferences */}
      <section
        data-testid="settings-notifications-section"
        className="rounded-lg border border-white/10 bg-[#121212] p-6"
      >
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-md grid place-items-center bg-gold/10 text-gold">
            <Bell className="w-4 h-4" />
          </div>
          <div>
            <div className="font-display text-xl leading-tight">Notification Preferences</div>
            <div className="text-xs text-zinc-500">
              These apply to <span className="text-zinc-300">you</span> on this production only.
            </div>
          </div>
        </div>

        {!prefs ? (
          <div className="text-xs text-zinc-500 py-6 text-center">Loading…</div>
        ) : (
          <div className="mt-5 space-y-6">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">
                Email digest cadence
              </Label>
              <div className="mt-2 max-w-md">
                <Select
                  value={prefs.email_digest_cadence || "off"}
                  onValueChange={(v) => patchPrefs({ email_digest_cadence: v })}
                >
                  <SelectTrigger
                    data-testid="settings-cadence-select"
                    className="bg-[#0A0A0A] border-white/10"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    {CADENCE.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1.5 text-[11px] text-zinc-500">
                  A single summary email rolls up everything below into one message.
                </p>
              </div>
            </div>

            <div className="border-t border-white/5 pt-5">
              <Label className="text-xs uppercase tracking-widest text-zinc-500 block mb-3">
                Individual alerts
              </Label>
              <div className="space-y-2">
                {NOTIF_TOGGLES.map((n) => (
                  <div
                    key={n.key}
                    data-testid={`settings-notif-${n.key}`}
                    className="flex items-center justify-between gap-4 rounded-md border border-white/5 bg-[#0A0A0A] px-4 py-3"
                  >
                    <div className="min-w-0">
                      <div className="text-sm">{n.label}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">{n.hint}</div>
                    </div>
                    <Switch
                      data-testid={`settings-notif-toggle-${n.key}`}
                      checked={!!prefs[n.key]}
                      onCheckedChange={(v) => patchPrefs({ [n.key]: v })}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Personal calendar sync */}
      <CalendarSync />

      {/* Iter 76 — Weekly rehearsal digest schedule (production-wide) */}
      {canManageDigest && (
        <section data-testid="settings-digest-section" className="rounded-lg border border-white/10 bg-[#121212] p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md grid place-items-center bg-gold/10 text-gold">
                <Bell className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display text-xl leading-tight">Weekly rehearsal digest</div>
                <div className="text-xs text-zinc-500">
                  Automatic Sunday-morning email that sends every opted-in cast member the week ahead. Branded with your theater logo when uploaded.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-widest text-zinc-500">{digest.weekly_digest_enabled ? "ON" : "OFF"}</span>
              <Switch
                data-testid="digest-enabled-switch"
                checked={!!digest.weekly_digest_enabled}
                onCheckedChange={(v) => saveDigest({ weekly_digest_enabled: v })}
              />
            </div>
          </div>

          <div className={`mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 ${digest.weekly_digest_enabled ? "" : "opacity-40 pointer-events-none"}`}>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Day of week</Label>
              <Select
                value={String(digest.weekly_digest_day)}
                onValueChange={(v) => saveDigest({ weekly_digest_day: parseInt(v, 10) })}
              >
                <SelectTrigger data-testid="digest-day-select" className="bg-[#0A0A0A] border-white/10 mt-2"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white">
                  {DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}day{i === 0 ? "" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Hour</Label>
              <Select
                value={String(digest.weekly_digest_hour)}
                onValueChange={(v) => saveDigest({ weekly_digest_hour: parseInt(v, 10) })}
              >
                <SelectTrigger data-testid="digest-hour-select" className="bg-[#0A0A0A] border-white/10 mt-2"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white max-h-72">
                  {Array.from({ length: 24 }, (_, h) => (
                    <SelectItem key={h} value={String(h)}>
                      {h === 0 ? "12:00 AM" : h < 12 ? `${h}:00 AM` : h === 12 ? "12:00 PM" : `${h - 12}:00 PM`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Timezone</Label>
              <Input
                data-testid="digest-timezone-input"
                value={digest.weekly_digest_timezone}
                onChange={(e) => setDigest((cur) => ({ ...cur, weekly_digest_timezone: e.target.value }))}
                onBlur={(e) => saveDigest({ weekly_digest_timezone: e.target.value })}
                placeholder="America/New_York"
                className="bg-[#0A0A0A] border-white/10 mt-2"
              />
            </div>
          </div>

          <div className="mt-4 text-[11px] text-zinc-500 space-y-1">
            <div>• Effective granularity in production is 15 minutes (cron budget). The digest fires within a 15-min window of the chosen hour.</div>
            <div>• Each cast member can opt in or out under <span className="text-zinc-300">Notification Preferences</span> below.</div>
            <div>• The email is branded with your uploaded theater logo (falls back to StageCall gold).</div>
            <div>• Last sent for this show: <span className="text-zinc-300">{production?.weekly_digest_last_sent_at ? new Date(production.weekly_digest_last_sent_at).toLocaleString() : "—"}</span>{digestSaving && <span className="ml-2 text-gold">saving…</span>}</div>
          </div>

          <div className="mt-4 pt-4 border-t border-white/5">
            <button
              data-testid="digest-preview-btn"
              onClick={async () => {
                try {
                  const { data } = await api.post(`/digest/preview?production_id=${productionId}`);
                  if (data.delivered) toast.success("Preview sent to your email");
                  else toast.error(`Preview generated but delivery failed: ${data.error || "provider refused"}`);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Couldn't send preview");
                }
              }}
              className="h-9 px-3 rounded border border-gold/40 text-gold text-sm hover:bg-gold/10 inline-flex items-center gap-1.5"
            >
              <Bell className="w-3.5 h-3.5" /> Send test digest to me
            </button>
            <span className="ml-3 text-[11px] text-zinc-500">Shows every call in the current week — a preview of what cast members will get on Sunday.</span>
          </div>
        </section>
      )}

      {/* Tag dialog */}
      <Dialog open={tagOpen} onOpenChange={setTagOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white" data-testid="settings-tag-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit tag" : "New tag"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Tags are shared across the whole production — everyone sees the same vocabulary.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Label</Label>
              <Input
                data-testid="settings-tag-label-input"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                autoFocus
                placeholder="e.g. Costume, Blocking, Music"
                className="bg-[#0A0A0A] border-white/10"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                <Palette className="w-3.5 h-3.5" /> Colour
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {SWATCH.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`settings-tag-swatch-${c.replace("#", "")}`}
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-7 h-7 rounded-full transition-transform hover:scale-110 ${
                      form.color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#121212]" : ""
                    }`}
                    style={{ backgroundColor: c }}
                    aria-label={`Select ${c}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label>Description <span className="text-zinc-600 text-xs">(optional)</span></Label>
              <Textarea
                data-testid="settings-tag-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="How should the team use this tag?"
                className="bg-[#0A0A0A] border-white/10"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid="settings-tag-save-btn"
              onClick={saveTag}
              style={goldBtn}
              className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110 inline-flex items-center gap-1.5"
            >
              <Save className="w-4 h-4" /> Save tag
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
